import React, { useState } from 'react'
import Header from './header'
import { useLocation } from 'react-router-dom'
import { ConvertorMiladi } from 'utilities/helper/digitToMonth'

// Android @drawable/info tinted @color/colorPrimary (#74B743):
// a filled green circle with a white 'i' glyph cut out.
function InfoMark() {
    return (
        <svg
            className=" shrink-0 mt-[3px]"
            width="16"
            height="16"
            viewBox="0 0 50 50"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle cx="25" cy="25" r="23" fill="#74B743" />
            <path
                d="M25,11A3,3 0,0 0,22 14A3,3 0,0 0,25 17A3,3 0,0 0,28 14A3,3 0,0 0,25 11zM21,21L21,23L22,23L23,23L23,36L22,36L21,36L21,38L22,38L23,38L27,38L28,38L29,38L29,36L28,36L27,36L27,21L26,21L22,21L21,21z"
                fill="#fff"
            />
        </svg>
    )
}

// Android rows (MoneypoolMemberDetailActivity): a single centered
// "label: value" line with the green (i) icon at the row's END (right).
// Tapping the row pops a green Balloon tooltip below it (showGuide()).
function InfoRow({
    id,
    guide,
    openGuide,
    setOpenGuide,
    children,
}: {
    id: string
    guide?: string
    openGuide: string | null
    setOpenGuide: (v: string | null) => void
    children: React.ReactNode
}) {
    const isOpen = !!guide && openGuide === id
    return (
        <div className=" relative mt-0.5 flex flex-col items-center">
            <button
                type="button"
                className=" flex items-start justify-center gap-1"
                onClick={() =>
                    guide && setOpenGuide(isOpen ? null : id)
                }
            >
                {guide && <InfoMark />}
                <span className=" text-sm text-black text-right">
                    {children}
                </span>
            </button>
            {isOpen && (
                <div
                    className=" absolute top-full z-30 mt-1.5 w-max max-w-[280px] rounded-[6px] bg-green-1 px-3 py-1.5 text-center text-[13px] leading-6 text-white shadow-pop"
                    onClick={() => setOpenGuide(null)}
                >
                    <span className=" absolute bottom-full left-1/2 -ml-2 h-0 w-0 border-x-8 border-b-8 border-x-transparent border-b-green-1" />
                    {guide}
                </div>
            )}
        </div>
    )
}

// Verbatim Android guide strings (mp_member_status_detail_guide_*)
const GUIDES = {
    capital: 'دارایی شخصی کاربر در صندوق',
    debts:
        'وام‌های تسویه‌نشده‌ی کاربر که از مجموع اقساط پرداخت نشده‌ی وی محاسبه می‌شود',
    joinDate: 'تاریخ عضویت در صندوق',
    intervalShareValue: 'میزان سهم تعهد شده برای واریز در هر دوره',
    intervalInstallments:
        'مجموع اقساطی که کاربر برای وام‌های دریافتی‌اش باید در هر دوره بپردازد',
    intervalCommitments:
        'مجموع تعهدات پرداخت‌نشده‌ی کاربر مانند حق‌عضویت پرداخت‌نشده این دوره، حق‌عضویت‌های با تأخیر در پرداخت، اقساط پرداخت نشده این دوره و اقساط معوق',
    receivedLoansCount: 'تعداد وام‌های دریافتی',
    totalLoansAmount: 'مجموع وام‌های دریافتی',
    unpaidInstallments: 'تعداد کل اقساط پرداخت‌نشده',
    delayedInstallments: 'تعداد اقساط پرداختنشده‌ی معوق',
    totalCashins:
        'بستانکاری دفتری: تمام واریزهای انجام شده توسط این عضو اعم از حق عضویت، واریزهای دیگر و اقساط پرداخت شده',
    totalCashouts:
        'بدهکاری دفتری: تمام مبالغ دریافت شده توسط این عضو شامل برداشت‌ها و وام‌های دریافتی',
}

interface IUserInfoProps {
    data: FinancialHistoryUser
}

export default function UserInfo({ data }: IUserInfoProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [openGuide, setOpenGuide] = useState<string | null>(null)
    const { state } = useLocation()

    const fmt = (x: number | string | null | undefined) =>
        Number(x || 0).toLocaleString()

    const unpaidInstallmentsCount =
        Number(data.number_of_installments || 0) -
        Number(data.number_of_paid_installments || 0)

    const intervalCommitments = Number(data.interval_commitments || 0)

    return (
        <div className=" bg-white px-2 pt-2">
            {/* member name */}
            <p className=" text-center text-base font-bold text-black mt-2">
                {data.member?.first_name} {data.member?.last_name}
            </p>

            {/* info box — Android light_gray_btn_bg: #EFEFEF fill,
                1dp #D4D4D4 stroke, 4dp radius, 8dp padding */}
            <div className=" mt-2 mx-2 bg-[#EFEFEF] border border-[#D4D4D4] rounded-[4px] p-2 flex flex-col items-stretch">
                <Header title="اطلاعات کلی" />

                <InfoRow
                    id="capital"
                    guide={GUIDES.capital}
                    openGuide={openGuide}
                    setOpenGuide={setOpenGuide}
                >
                    سرمایه شخصی: {fmt(data.capital)} تومان
                </InfoRow>

                <InfoRow
                    id="debts"
                    guide={GUIDES.debts}
                    openGuide={openGuide}
                    setOpenGuide={setOpenGuide}
                >
                    مجموع بدهی: {fmt(data.debit_amount)} تومان
                </InfoRow>

                <InfoRow
                    id="joinDate"
                    guide={GUIDES.joinDate}
                    openGuide={openGuide}
                    setOpenGuide={setOpenGuide}
                >
                    تاریخ عضویت:{' '}
                    {!!data.join_datetime &&
                        ConvertorMiladi(data.join_datetime)}
                </InfoRow>

                {data && data.guarantor && (
                    <InfoRow
                        id="guarantor"
                        openGuide={openGuide}
                        setOpenGuide={setOpenGuide}
                    >
                        ضامن عضو: {data.guarantor.member.first_name}{' '}
                        {data.guarantor.member.last_name}
                    </InfoRow>
                )}

                {data && data.recommender && (
                    <InfoRow
                        id="recommender"
                        openGuide={openGuide}
                        setOpenGuide={setOpenGuide}
                    >
                        معرف عضو: {data.recommender.member.first_name}{' '}
                        {data.recommender.member.last_name}
                    </InfoRow>
                )}

                {isOpen && (
                    <>
                        <Header title="تعهدات" />
                        <InfoRow
                            id="intervalShareValue"
                            guide={GUIDES.intervalShareValue}
                            openGuide={openGuide}
                            setOpenGuide={setOpenGuide}
                        >
                            حق عضویت این دوره:{' '}
                            {fmt(
                                Number(state?.shareValueAmount || 0) *
                                    Number(data.number_of_shares || 0)
                            )}{' '}
                            تومان معادل {fmt(data.number_of_shares)} سهم
                        </InfoRow>
                        <InfoRow
                            id="intervalInstallments"
                            guide={GUIDES.intervalInstallments}
                            openGuide={openGuide}
                            setOpenGuide={setOpenGuide}
                        >
                            اقساط این دوره:{' '}
                            {fmt(data.normal_unpaid_installments_amount)} تومان
                        </InfoRow>
                        <InfoRow
                            id="intervalCommitments"
                            guide={GUIDES.intervalCommitments}
                            openGuide={openGuide}
                            setOpenGuide={setOpenGuide}
                        >
                            تعهدات پرداخت‌نشده:{' '}
                            <span
                                style={
                                    intervalCommitments > 0
                                        ? { color: '#F44336' }
                                        : undefined
                                }
                            >
                                {fmt(intervalCommitments)}
                            </span>{' '}
                            تومان
                        </InfoRow>

                        <Header title="وام‌ها" />
                        <InfoRow
                            id="receivedLoansCount"
                            guide={GUIDES.receivedLoansCount}
                            openGuide={openGuide}
                            setOpenGuide={setOpenGuide}
                        >
                            تعداد وام‌های دریافتی: {fmt(data.num_loans)} وام
                        </InfoRow>
                        <InfoRow
                            id="totalLoansAmount"
                            guide={GUIDES.totalLoansAmount}
                            openGuide={openGuide}
                            setOpenGuide={setOpenGuide}
                        >
                            مجموع وام‌های دریافتی: {fmt(data.loans_amount)}{' '}
                            تومان
                        </InfoRow>
                        <InfoRow
                            id="unpaidInstallments"
                            guide={GUIDES.unpaidInstallments}
                            openGuide={openGuide}
                            setOpenGuide={setOpenGuide}
                        >
                            تعداد اقساط پرداخت‌نشده:{' '}
                            {fmt(unpaidInstallmentsCount)} قسط
                        </InfoRow>
                        <InfoRow
                            id="delayedInstallments"
                            guide={GUIDES.delayedInstallments}
                            openGuide={openGuide}
                            setOpenGuide={setOpenGuide}
                        >
                            تعداد اقساط معوق:{' '}
                            {fmt(data.number_of_delayed_installments)} قسط
                        </InfoRow>

                        <Header title="اطلاعات دفتری" />
                        <InfoRow
                            id="totalCashins"
                            guide={GUIDES.totalCashins}
                            openGuide={openGuide}
                            setOpenGuide={setOpenGuide}
                        >
                            مجموع واریزها: {fmt(data.asset_value)} تومان
                        </InfoRow>
                        <InfoRow
                            id="totalCashouts"
                            guide={GUIDES.totalCashouts}
                            openGuide={openGuide}
                            setOpenGuide={setOpenGuide}
                        >
                            مجموع برداشت‌ها: {fmt(data.debt_value)} تومان
                        </InfoRow>
                    </>
                )}
            </div>

            {/* more / less info toggle button attached to bottom of box */}
            <div className=" flex justify-center -mt-px">
                <div
                    className=" flex items-center justify-center gap-2 bg-[#EFEFEF] border border-[#D4D4D4] rounded-b-[4px] px-2 py-1 cursor-pointer"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {/* Android @drawable/ic_up = ic_arrow_left_black rotated 90deg,
                        tinted @color/hamyan_blue_500 (#2196F3). Flips on toggle. */}
                    <svg
                        className=" w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                            transform: isOpen ? 'none' : 'rotate(180deg)',
                        }}
                    >
                        <path
                            d="M6 14L12 8L18 14"
                            stroke="#2196F3"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span className=" text-sm font-bold text-[#2196F3]">
                        {isOpen ? 'اطلاعات کمتر' : 'اطلاعات بیشتر'}
                    </span>
                </div>
            </div>
        </div>
    )
}
